import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, FilePlus2, Eye, Pencil, Trash2, Share2, Calendar, IndianRupee, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "invoice", label: "Invoices" },
  { id: "quotation", label: "Quotations" },
  { id: "shared", label: "Shared with me" },
];

const STATUS_COLORS = {
  draft: "bg-lavender text-plum",
  sent: "bg-rose/15 text-rose",
  paid: "bg-emerald-100 text-emerald-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700",
  void: "bg-gray-100 text-gray-600",
};

export default function Documents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("invoice");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const url = tab === "shared" ? "/documents/shared-with-me" : `/documents?doc_type=${tab}`;
      const r = await api.get(url);
      setDocs(r.data);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const remove = async (id) => {
    if (!confirm("Delete this document? This cannot be undone.")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const role = user?.role === "admin" ? "admin" : "member";

  return (
    <DashboardShell
      role={role}
      title="Documents"
      subtitle="Invoices & quotations · auto-branded with your profile."
      actions={(
        <div className="flex items-center gap-2">
          <button data-testid="new-quotation-btn" onClick={() => navigate("/documents/new?type=quotation")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-aubergine/20 text-aubergine text-xs uppercase tracking-widest hover:bg-aubergine hover:text-ivory transition-all">
            <FilePlus2 className="w-3.5 h-3.5" /> New quotation
          </button>
          <button data-testid="new-invoice-btn" onClick={() => navigate("/documents/new?type=invoice")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aubergine text-ivory text-xs uppercase tracking-widest hover:bg-aubergineLight">
            <FilePlus2 className="w-3.5 h-3.5" /> New invoice
          </button>
        </div>
      )}
    >
      <div data-testid="documents-page">
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`doc-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest border ${tab === t.id ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum hover:bg-aubergine/5"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-plumSoft">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="py-20 text-center bg-gradient-to-br from-lavender/40 to-white rounded-2xl border border-dashed border-aubergine/15">
            <Inbox className="w-10 h-10 text-plum mx-auto opacity-60" />
            <h3 className="font-display text-2xl text-aubergine mt-4">
              {tab === "shared" ? "Nothing shared with you yet" : `No ${tab === "invoice" ? "invoices" : "quotations"} yet`}
            </h3>
            <p className="text-sm text-plumSoft mt-2 max-w-md mx-auto font-light">
              {tab === "shared" ? "Invoices & quotations from other members will land here when shared." : "Create your first one — we'll auto-pull your company info from your profile."}
            </p>
            {tab !== "shared" && (
              <button onClick={() => navigate(`/documents/new?type=${tab}`)} className="btn-primary mt-5 inline-flex">Create {tab === "invoice" ? "invoice" : "quotation"}</button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {docs.map((d) => (
              <article key={d.id} data-testid={`doc-row-${d.id}`} className="bg-white rounded-2xl border border-aubergine/8 p-5 flex flex-wrap items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-lavender/60 text-plum flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-display text-lg text-aubergine">{d.number} · <span className="text-plumSoft text-base font-light">{d.recipient?.name || "—"}</span></p>
                  <p className="text-xs text-plum mt-1 flex flex-wrap gap-3">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {d.issue_date}</span>
                    <span className="flex items-center gap-1.5"><IndianRupee className="w-3 h-3" /> {Number(d.totals?.grand_total || 0).toLocaleString("en-IN")}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${STATUS_COLORS[d.status] || STATUS_COLORS.draft}`}>{d.status}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Link to={`/documents/${d.id}`} data-testid={`doc-view-${d.id}`} className="p-2 rounded-lg text-plum hover:bg-lavender/60" title="View"><Eye className="w-4 h-4" /></Link>
                  {tab !== "shared" && (
                    <>
                      <Link to={`/documents/${d.id}/edit`} data-testid={`doc-edit-${d.id}`} className="p-2 rounded-lg text-plum hover:bg-lavender/60" title="Edit"><Pencil className="w-4 h-4" /></Link>
                      {d.recipient?.type === "member" && !d.shared_with_member_id && (
                        <button data-testid={`doc-share-${d.id}`} title="Share with member" onClick={async () => { try { await api.post(`/documents/${d.id}/share`); toast.success("Shared"); load(); } catch { toast.error("Could not share"); } }} className="p-2 rounded-lg text-plum hover:bg-lavender/60"><Share2 className="w-4 h-4" /></button>
                      )}
                      <button data-testid={`doc-delete-${d.id}`} title="Delete" onClick={() => remove(d.id)} className="p-2 rounded-lg text-plum hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
